import React, { useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import { formatRelativeAge } from '../../../../../lib/ui/timeUtils';

// Roomier than Bootstrap's 0.65em sides, so the badge doesn't hug its text and
// stays steady when the shorter age swaps in.
const SIDE_PADDING = '1.1em';

// Under 100ms: the swap should feel immediate, just not abrupt.
const TRANSITION = 'box-shadow 90ms ease, color 90ms ease, font-size 90ms ease';

// Hover styling. The lightening is an inset shadow rather than a background
// colour so it works over any bg-* variant (which carry !important) and paints
// under the text; both it and the colour animate, unlike a gradient would.
const HOVER_LIGHTEN = 'inset 0 0 0 999px rgba(255, 255, 255, 0.3)';
const HOVER_COLOR = '#1c2b3a';
const HOVER_FONT_SCALE = 0.92;
const BOOTSTRAP_BADGE_FONT_SIZE = '0.75em'; // .badge's own default

function scaleFontSize(fontSize, scale) {
    const match = String(fontSize).match(/^([\d.]+)(rem|em|px)$/);
    if (!match) return undefined;
    return `${+(parseFloat(match[1]) * scale).toFixed(4)}${match[2]}`;
}

/**
 * A timestamp pill that shows how long ago it was ("4h 47m ago") while the
 * pointer is over it, and the absolute time otherwise. The absolute text is
 * passed in, since each feed formats it differently (full date here,
 * time-of-day in the event stream); `value` is the raw timestamp the age is
 * computed from.
 *
 * The tooltip keeps the absolute time visible during the hover, so both
 * readings are available at once. The age is computed at render, so it
 * refreshes whenever the surrounding view re-renders (and on each hover)
 * rather than ticking per row.
 *
 * `label` names what the timestamp is ("Created") and prefixes the tooltip.
 */
function TimestampBadge({ value, absolute, label, style = {}, ...badgeProps }) {
    const [hovered, setHovered] = useState(false);

    const relative = formatRelativeAge(value);
    // Nothing to swap in if the timestamp is unusable — stay a plain badge.
    const showingRelative = Boolean(relative) && hovered;

    const baseStyle = {
        userSelect: 'none',
        paddingLeft: SIDE_PADDING,
        paddingRight: SIDE_PADDING,
        transition: TRANSITION,
        ...style,
    };

    if (showingRelative) {
        baseStyle.boxShadow = HOVER_LIGHTEN;
        baseStyle.color = HOVER_COLOR;
        // Scale whatever size the call site asked for; fall back to the badge
        // default so a call site that sets none still shrinks proportionally.
        const smaller = scaleFontSize(style.fontSize || BOOTSTRAP_BADGE_FONT_SIZE, HOVER_FONT_SCALE);
        if (smaller) baseStyle.fontSize = smaller;
    }

    const title = [label, absolute].filter(Boolean).join(' · ') || undefined;

    return (
        <Badge
            {...badgeProps}
            style={baseStyle}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            title={title}
        >
            {showingRelative ? relative : absolute}
        </Badge>
    );
}

export default TimestampBadge;
